import { Card, Flex, Tooltip, Typography } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, ClockCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { TinyLine } from "@ant-design/plots";
import type { DashboardPeriod } from "../../utils";
import { estadoDeWidget, WidgetPlaceholder } from "../widget-state";
import type { WidgetDataProps } from "../../types";

const { Text } = Typography;

const CARD_HEAD   = 38;
const PAD_V       = 24;
const NUMBER_H    = 52;
const LABEL_H     = 20;
const GAP_H       = 10;

// TODO: la card pasó a medir la resolución de las CONVERSACIONES, dato que sale de
// conversation-service y todavía no se expone. Hasta que el backend lo entregue no se muestra el
// número de antes (presupuestos, de Store a Comprado), que no es lo que dice el título.
const HAY_DATO_DE_CONVERSACIONES = false;

type PeriodEntry = { days: number; prev: number; trend: number[]; trendLabel: string };


type Datos = {
  casos: number;
  diasPromedio: number | null;
  anterior: number | null;
  serie: { date: string; diasPromedio: number | null }[];
};

export const TiempoPromedioCierreChip = ({
  height = 240,
  period = "day",
  data,
  estado,
  error,
  nuncaCalculado,
  recargar,
}: { height?: number; period?: DashboardPeriod } & WidgetDataProps<Datos>) => {
  const days  = Math.round(data?.diasPromedio ?? 0);
  const prev  = Math.round(data?.anterior ?? 0);
  // Los días sin ningún cierre no aportan al promedio y romperían la línea: se saltean.
  const trend = (data?.serie ?? [])
    .filter((d) => d.diasPromedio !== null)
    .map((d) => Number(d.diasPromedio));
  const trendLabel = "Evolución en el período (días)";
  const status = estadoDeWidget(estado, error, nuncaCalculado, (data?.casos ?? 0) > 0, recargar);
  const bodyH  = height - CARD_HEAD;
  const chartH = Math.max(30, bodyH - PAD_V - NUMBER_H - GAP_H - LABEL_H);

  const diff  = days - prev;
  const mejor = diff < 0;
  const color = mejor ? "#15744b" : "#cf1322";
  const Icon  = mejor ? ArrowDownOutlined : ArrowUpOutlined;
  const pct   = prev > 0 ? Math.round(Math.abs(diff / prev) * 100) : 0;

  return (
    <Card
      size="small"
      style={{ height }}
      styles={{ body: { padding: "12px 16px", height: bodyH, overflow: "hidden", display: "flex", flexDirection: "column" } }}
      title={
        <Flex align="center" gap={8}>
          <ClockCircleOutlined style={{ fontSize: 14 }} />
          <span style={{ fontWeight: 600 }}>Tiempo de resolución de la conversación</span>
          <Tooltip
            title="Tiempo promedio que tarda una conversación en resolverse, desde que se abre hasta que se cierra."
            trigger="click"
            color="#fff"
            overlayInnerStyle={{ color: "rgba(0,0,0,0.85)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            overlayStyle={{ maxWidth: 240 }}
          >
            <InfoCircleOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        </Flex>
      }
      extra={
        <Text type="secondary" style={{ fontSize: 11 }}>
          {({ day: "Hoy", week: "Esta semana", prev_week: "Semana anterior", month: "Este mes", prev_month: "Mes anterior" } as Record<string, string>)[period] ?? period}
        </Text>
      }
    >
      {!HAY_DATO_DE_CONVERSACIONES ? (
        <Flex align="center" justify="center" style={{ height: bodyH - 20 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Sin datos para mostrar</Text>
        </Flex>
      ) : status.tipo !== "ok" ? (
        <WidgetPlaceholder status={status} height={bodyH - 20} />
      ) : (
        <>
      {/* Big metric */}
      <div style={{ flexShrink: 0 }}>
        <Flex align="baseline" gap={8} style={{ marginBottom: 2 }}>
          <Text strong style={{ fontSize: 32, lineHeight: 1 }}>{days}</Text>
          <Text style={{ fontSize: 16, color: "rgba(0,0,0,0.35)" }}>días</Text>
          <span style={{ fontSize: 11, color, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 2 }}>
            <Icon style={{ fontSize: 9 }} />
            {mejor ? "-" : "+"}{pct}% vs ant.
          </span>
        </Flex>
        <Text type="secondary" style={{ fontSize: 11 }}>
          Store → Comprado · anterior {prev} días
        </Text>
      </div>

      {/* Sparkline */}
      <div style={{ marginTop: GAP_H, flexShrink: 0 }}>
        <Text style={{ fontSize: 10, color: "rgba(0,0,0,0.3)", display: "block", marginBottom: 4 }}>
          {trendLabel}
        </Text>
        <div style={{ height: chartH, overflow: "hidden" }}>
          <TinyLine
            data={trend}
            autoFit
            smooth
            color="#1677ff"
            lineStyle={{ lineWidth: 1.5 }}
            tooltip={false}
            animation={false}
          />
        </div>
      </div>
            </>
      )}
    </Card>
  );
};
